import React, {Fragment, useEffect, useMemo, useState} from 'react'
import localization from "../../../../../assets/localization/localization";
import TableSection from "../../../../../components/sales/tableSection/tableSection";
import Layout from "../../../../../components/sales/layout/layout";
import {Button} from "react-bootstrap";
import Images from "../../../../../assets/images/imageData";
import Filter from "../Filter";
import {toast } from "react-toastify";
import { AppliedFiltersBar } from "components/UI";

const FILTER_STORAGE_KEY = "variable_service_invoice_filter";

const EMPTY_FILTERS = {
    invoice_no: "",
    installment_status: null,
    billing_type: null,
    ti_number: "",
    site_number: "",
    client_name: "",
    from_pi_date: "",
    to_pi_date: "",
};

const readSavedListState = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY) || "null");
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return { filters: { ...EMPTY_FILTERS }, page: 1 };
        }
        const filters = parsed.filters && typeof parsed.filters === "object"
            ? parsed.filters
            : parsed;
        return {
            filters: { ...EMPTY_FILTERS, ...filters },
            page: Number(parsed.page) > 0 ? Number(parsed.page) : 1,
        };
    } catch (error) {
        return { filters: { ...EMPTY_FILTERS }, page: 1 };
    }
};

const persistListState = (filters, page) => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({ filters, page }));
};

const ServiceInvoiceList = ({
    getServiceInvoiceList,
    valetContractInvoice=[]
}) => {
    const savedListState = readSavedListState();
    const [listData, setListData] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(savedListState.page);
    const [showFilter, setShowFilter] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(savedListState.filters);

    useEffect(() => {
        (async () => {
            setIsLoading(true)
            await getServiceInvoiceList(savedListState.page, 2, savedListState.filters)
            setIsLoading(false)
        })()
    }, []);

    const headers = [
        'PI Number',
        "TI Number",
        'PI Date',
        'TI Date',
        'Business Name',
        'Client Name',
        'Site Number',
        'Type',
        'Invoice Stage',
        'Status',
        'Actions'
    ];

    useEffect(() => {
        if (!valetContractInvoice || Array.isArray(valetContractInvoice) || !Array.isArray(valetContractInvoice.resultSets)) {
            return;
        }
        if (valetContractInvoice.resultSets.length === 0) {
            setListData([]);
            setPageCount(0);
            return;
        }
        if (valetContractInvoice?.resultSets.length > 0) {
            const tableData = [
                'invoice_number', 
                'ti_number', 
                'invoice_date',
                'ti_date', 
                'business_name', 
                'client_name',
                'site_number', 
                'billing_type',
                'invoice_stage',
                'installment_status'
            ];

            const data = valetContractInvoice?.resultSets.map((element) => {
                const mappedData = Object.assign(
                    ...tableData.map((key) => (
                        key === 'billing_type' ? 
                            {[key]: element[key] === 1 ? 'PREPAID' : 'POSTPAID'} : 
                        {[key]: element[key]}
                    )),
                    {}
                );            

                const actions = [];
    
                // if (element.installment_status !== 'PENDING') {
                //     actions.push({ name: 'View', link: `service-approval-action-invoice-list/${element['invoice_id']}` });
                // }
    
                if (!element.flag) {
                    actions.unshift({name: 'Convert to TI', link: `service-variable-invoice-edit/${element['invoice_id']}/${element['installment_type']}`});
                }
    
                return {...mappedData, Action: actions};
            });
    
            if (data) {
                setListData(data);
            }
    
            setPageCount(Math.ceil(valetContractInvoice.total / valetContractInvoice.per_page));
        }
    }, [valetContractInvoice]);
    

    const onPageChange = async (val) => {
        const nextPage = val.selected + 1;
        setCurrentPage(nextPage);
        persistListState(appliedFilters, nextPage);
        setIsLoading(true);
        await getServiceInvoiceList(nextPage, 2, appliedFilters);
        setIsLoading(false);
    };

    const applyFilter = async (filterData) => {
        const { from_pi_date, to_pi_date } = filterData;

        if ((from_pi_date && !to_pi_date) || (!from_pi_date && to_pi_date)) {
            toast.error("Please select both From PI Date and To PI Date.");
            return false;
        }

        const nextFilters = { ...EMPTY_FILTERS, ...filterData };
        setAppliedFilters(nextFilters);
        setCurrentPage(1);
        persistListState(nextFilters, 1);
        setIsLoading(true);
        await getServiceInvoiceList(1, 2, nextFilters);
        setIsLoading(false);
        filterToggle();
    };

    const clearFilters = async () => {
        setAppliedFilters({ ...EMPTY_FILTERS });
        setCurrentPage(1);
        localStorage.removeItem(FILTER_STORAGE_KEY);
        setIsLoading(true);
        await getServiceInvoiceList(1, 2);
        setIsLoading(false);
        if (showFilter) {
            setShowFilter(false);
        }
    };

    const appliedFilterLabels = useMemo(() => {
        const labels = [];
        if (appliedFilters.invoice_no) labels.push(`PI Number: ${appliedFilters.invoice_no}`);
        if (appliedFilters.site_number) labels.push(`Site Number: ${appliedFilters.site_number}`);
        if (appliedFilters.client_name) labels.push(`Client Name: ${appliedFilters.client_name}`);
        if (appliedFilters.ti_number) labels.push(`TI Number: ${appliedFilters.ti_number}`);
        if (appliedFilters.installment_status) labels.push(`Status: ${appliedFilters.installment_status}`);
        if (appliedFilters.billing_type != null && appliedFilters.billing_type !== "") {
            const billingName = String(appliedFilters.billing_type) === "1"
                ? "Prepaid"
                : String(appliedFilters.billing_type) === "2"
                    ? "Postpaid"
                    : appliedFilters.billing_type;
            labels.push(`Type: ${billingName}`);
        }
        if (
            appliedFilters.from_pi_date &&
            appliedFilters.to_pi_date &&
            appliedFilters.from_pi_date !== "Invalid date" &&
            appliedFilters.to_pi_date !== "Invalid date"
        ) {
            labels.push(`PI Date: ${appliedFilters.from_pi_date} - ${appliedFilters.to_pi_date}`);
        }
        return labels;
    }, [appliedFilters]);

    const filterToggle = () => {
        setShowFilter(!showFilter);
    };

    const headerBtns = () => {
        return (
            <>
                <Fragment>
                    <Button
                        onClick={filterToggle}
                        className="filter-hamburger btn btn-transparent"
                    >
                        <img src={Images?.Filter} alt=""></img>
                        {localization.showFilters}
                    </Button>
                </Fragment>
            </>
        );
    };

    return (
        <>
            <Layout
                title={'Variable Service Invoice'}
                isLoading={isLoading}
                isGoback={true}
                Buttons={headerBtns}
            >
                <AppliedFiltersBar
                    items={appliedFilterLabels}
                    onClear={clearFilters}
                    clearButtonLabel="Remove filter"
                />
                <TableSection
                    data={listData}
                    isId={false}
                    isMngId={false}
                    headers={headers}
                    pageCount={pageCount}
                    currentPage={currentPage}
                    onPageChange={onPageChange}
                    detailList={valetContractInvoice && valetContractInvoice.resultSets}
                    srNo={valetContractInvoice?.from}
                />
            </Layout>

            <Filter
                showFilter={showFilter}
                filterToggle={filterToggle}
                applyFilter={(val) => applyFilter(val)}
                appliedFilterValues={appliedFilters}
            />

        </>
    )
}

export default ServiceInvoiceList